import React, { useState, useEffect } from 'react';
import { AlertCircle, Package, X, ChevronRight, Bell, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import marketplaceAPI from '../../services/marketplace.api';

const InventoryAlert = ({ onViewProduct, onRefresh }) => {
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    setIsLoading(true);
    try {
      const response = await marketplaceAPI.getInventoryAlerts({ unreadOnly: true });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const dismissAlert = async (alertId) => {
    try {
      await marketplaceAPI.markNotificationsAsRead([alertId]);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      toast.success('Alert dismissed');
      
      // Call onRefresh if provided
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to dismiss alert');
    }
  };

  const dismissAllAlerts = async () => {
    try {
      const alertIds = alerts.map(a => a.id);
      await marketplaceAPI.markNotificationsAsRead(alertIds);
      setAlerts([]);
      toast.success('All alerts dismissed');
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to dismiss alerts');
    }
  };

  const handleViewProduct = (productId, businessId) => {
    // First dismiss the alert
    const alert = alerts.find(a => a.data?.productId === productId);
    if (alert) {
      dismissAlert(alert.id);
    }
    
    // Navigate to the product management page
    if (businessId) {
      navigate(`/marketplace/business/${businessId}/products/manage`);
    } else {
      // Fallback to product list if business ID not available
      navigate('/marketplace/products/manage');
    }
    
    // If onViewProduct prop was provided, call it too
    if (onViewProduct) {
      onViewProduct(productId);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl p-4 shadow-neomorph border border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
            <h3 className="text-sm font-semibold text-gray-800">Inventory Alerts</h3>
          </div>
        </div>
        <div className="flex items-center justify-center py-4">
          <RefreshCw className="h-5 w-5 animate-spin text-primary-600" />
          <span className="ml-2 text-sm text-gray-500">Loading alerts...</span>
        </div>
      </div>
    );
  }

  if (alerts.length === 0) return null;

  return (
    <div className="bg-white rounded-xl p-4 shadow-neomorph border border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
          <h3 className="text-sm font-semibold text-gray-800 flex items-center">
            <Bell className="h-4 w-4 mr-1.5 text-primary-600" />
            Inventory Alerts
          </h3>
        </div>
        
        {alerts.length > 1 && (
          <button
            onClick={dismissAllAlerts}
            className="text-xs text-gray-500 hover:text-gray-700 bg-[#E8ECF5] px-2 py-1 rounded-lg shadow-neomorph-inset"
          >
            Dismiss all
          </button>
        )}
      </div>

      {/* Alerts */}
      <div className="space-y-3">
        {alerts.map(alert => (
          <div
            key={alert.id}
            className="bg-yellow-50 rounded-xl p-4 shadow-neomorph-inset border border-yellow-200/50 relative"
          >
            <button
              onClick={() => dismissAlert(alert.id)}
              className="absolute top-3 right-3 p-1 bg-yellow-100 rounded-lg shadow-neomorph-inset hover:shadow-neomorph transition-all text-yellow-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            
            <div className="flex items-start pr-8">
              <div className="p-2 bg-yellow-100 rounded-lg shadow-neomorph-inset mr-3">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-yellow-800 mb-1">{alert.title}</h4>
                <p className="text-xs text-yellow-700 mb-2">{alert.message}</p>
                
                {/* Additional info if available */}
                {alert.data?.currentStock !== undefined && alert.data?.threshold !== undefined && (
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-lg shadow-neomorph-inset">
                      Current: {alert.data.currentStock}
                    </span>
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-lg shadow-neomorph-inset">
                      Threshold: {alert.data.threshold}
                    </span>
                  </div>
                )}
                
                <button
                  onClick={() => handleViewProduct(
                    alert.data?.productId, 
                    alert.data?.businessId
                  )}
                  className="inline-flex items-center text-xs font-medium text-yellow-800 bg-yellow-100 px-3 py-1.5 rounded-lg shadow-neomorph-inset hover:shadow-neomorph transition-all"
                >
                  View Product
                  <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default InventoryAlert;